import React, { useState, useEffect, useCallback } from 'react';
import {
  Banknote, ChevronLeft, ChevronRight, AlertTriangle, Loader2, CalendarRange, ShieldOff,
  Download, History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import BrandedToaster from '../components/BrandedToaster';
import { payrollApi, saveBlobResponse } from '../lib/api';
import { toast } from 'sonner';

// The format chooser. ⚠️ A CSV can only ever be ONE table, so the choice says
// which sheet you're getting rather than pretending both fit.
const EXPORT_FORMATS = [
  { key: 'xlsx', label: 'Excel — both sheets', hint: '.xlsx' },
  { key: 'csv_summary', label: 'CSV — summary only', hint: '.csv' },
  { key: 'csv_detail', label: 'CSV — detail only', hint: '.csv' },
];

/**
 * Payroll report (DESIGN-payroll.md stage 2a).
 *
 * One line per employee, with the pay-affecting numbers as columns and the rows
 * that need attention flagged and sorted to the top — Rob's call, and the right
 * one: payroll acts on what CHANGED, and forty rows of paid holiday is where the
 * two that matter get lost.
 *
 * The dated detail lives behind the summary rather than in front of it, for
 * whoever keys sickness into Sage or BrightPay.
 *
 * ⚠️ No reasons appear anywhere on this screen, by construction — the API never
 * sends them. Sickness reasons are Article 9 data and payroll does not need them
 * to pay anybody.
 */

// DD/MM/YYYY per CONVENTIONS.md. A function declaration at module scope so it
// cannot be read above its own definition.
function ukDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function num(v, dp = 2) {
  if (!v) return '—';
  const n = Number(v);
  if (!n) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(dp).replace(/\.?0+$/, '');
}

function treatmentLabel(t) {
  if (t === 'unpaid') return 'Unpaid — deduct';
  if (t === 'statutory') return 'Statutory — payroll decides';
  return 'Paid as normal';
}

function treatmentClass(t) {
  if (t === 'unpaid') return 'bg-rose-500/15 text-rose-600';
  if (t === 'statutory') return 'bg-amber-500/15 text-amber-600';
  return 'bg-gray-500/15 text-gray-500';
}

const PayrollPage = () => {
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [tab, setTab] = useState('summary');
  const [includeEveryone, setIncludeEveryone] = useState(false);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollApi.getReport({ offset, includeEveryone });
      setReport(res.data);
      setDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        // Not an error to shout about — this person simply doesn't run payroll.
        setDenied(true);
      } else {
        toast.error('Could not load the payroll report');
      }
    } finally {
      setLoading(false);
    }
  }, [offset, includeEveryone]);

  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const doExport = async (format) => {
    setExporting(format);
    setMenuOpen(false);
    try {
      const res = await payrollApi.exportReport({ format, offset, includeEveryone });
      const name = saveBlobResponse(res, `payroll.${format === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
      // ⚠️ Reload so the "you exported this period" banner appears straight away.
      // Without it the export you just ran is invisible until you navigate away
      // and come back, which reads as the audit trail not working.
      load();
    } catch (err) {
      // ⚠️ The error body is a BLOB here (responseType: 'blob'), so the usual
      // err.response.data.detail is a Blob object, not a string. Read it back.
      let message = 'Could not build the export';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setExporting(null);
    }
  };

  const period = report?.period;
  const totals = report?.totals;

  // Attention first, then by name — the sort the whole layout exists to serve.
  const summary = [...(report?.summary || [])].sort((a, b) => {
    if (a.needs_attention !== b.needs_attention) return a.needs_attention ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <BrandedToaster />
      <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Banknote className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl">Payroll</h1>
            <p className="text-sm text-muted-foreground">
              Approved absence for a pay period
            </p>
          </div>
        </div>

        {denied ? (
          <div className="card-hive p-10 text-center">
            <ShieldOff className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-60" />
            <p className="font-medium">You don't have permission to run payroll reports</p>
            <p className="text-sm text-muted-foreground mt-1">
              An admin can switch this on for you from your profile.
            </p>
          </div>
        ) : (
          <div className="card-hive overflow-hidden">
            {/* Period picker */}
            <div className="p-4 md:p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setOffset((o) => o - 1)}
                        data-testid="payroll-prev-period" aria-label="Previous period">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-4 py-1.5 rounded-lg border border-border bg-secondary/30 text-center min-w-[210px]">
                  <p className="font-semibold text-sm">{period?.label || '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {period ? `${ukDate(period.start)} – ${ukDate(period.end)}` : ''}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setOffset((o) => o + 1)}
                        data-testid="payroll-next-period" aria-label="Next period">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                {offset !== 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setOffset(0)}>
                    <CalendarRange className="w-4 h-4 mr-1" /> This period
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeEveryone}
                    onChange={(e) => setIncludeEveryone(e.target.checked)}
                    className="w-4 h-4 rounded border-input accent-primary"
                    data-testid="payroll-include-everyone"
                  />
                  Show everyone, including people with no absence
                </label>

                <div className="relative">
                  <Button
                    size="sm"
                    className="btn-primary"
                    onClick={() => setMenuOpen((o) => !o)}
                    disabled={!!exporting || loading}
                    data-testid="payroll-export-btn"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                               : <Download className="w-4 h-4 mr-2" />}
                    Export
                  </Button>
                  {menuOpen && (
                    <>
                      {/* Click-away, so the menu can't be left open behind a toast. */}
                      <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                      <div className="absolute right-0 mt-1 z-20 w-64 rounded-xl border border-border bg-card shadow-lg p-1">
                        {EXPORT_FORMATS.map((f) => (
                          <button
                            key={f.key}
                            onClick={() => doExport(f.key)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm hover:bg-secondary/60 text-left"
                            data-testid={`payroll-export-${f.key}`}
                          >
                            <span>{f.label}</span>
                            <span className="text-xs text-muted-foreground">{f.hint}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/*
              ⚠️ DECISION F2 — you have run this period before, and things have
              moved since. If a holiday is cancelled after payroll has run, the
              money is already out of the door; the file cannot undo that, but
              somebody has to NOTICE. With no memory at all, nobody does.
            */}
            {report?.previous_export && (
              <div className="flex items-start gap-3 px-4 py-3 border-b border-border bg-blue-500/5"
                   data-testid="payroll-previous-export">
                <History className="w-4 h-4 text-blue-600 mt-0.5 flex-none" />
                <p className="text-sm text-blue-700">
                  You exported this period on {ukDate((report.previous_export.at || '').slice(0, 10))}
                  {report.previous_export.by ? ` (${report.previous_export.by})` : ''}.
                  {report.previous_export.changed_since > 0 ? (
                    <span className="font-semibold">
                      {' '}{report.previous_export.changed_since} absence
                      {report.previous_export.changed_since === 1 ? ' has' : 's have'} changed since
                      — check anything already paid.
                    </span>
                  ) : ' Nothing has changed since.'}
                </p>
              </div>
            )}

            {loading ? (
              <div className="p-16 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Headline counts */}
                <div className="flex flex-wrap gap-2 p-4 bg-amber-500/5 border-b border-border">
                  {[
                    ['need your attention', totals?.needs_attention],
                    ['people in this period', totals?.people_in_period],
                    ['unpaid days to deduct', totals?.unpaid_days],
                    ['leavers to settle', totals?.leavers],
                    ['birthdays this period', totals?.birthdays],
                    ['missing a payroll reference', totals?.missing_payroll_ids],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-background border border-amber-500/30 rounded-lg px-3 py-2">
                      <p className="text-lg font-bold text-amber-600 leading-none">{value ?? 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 px-4 border-b border-border bg-secondary/20">
                  {[['summary', 'Summary — one line per person'],
                    ['detail', 'Detail — one line per absence']].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        tab === key
                          ? 'border-amber-500 text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`payroll-tab-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  {tab === 'summary' ? (
                    <table className="w-full text-sm min-w-[900px]">
                      <thead>
                        <tr className="bg-secondary/30 text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="w-8"></th>
                          <th className="text-left p-3 font-semibold">Ref</th>
                          <th className="text-left p-3 font-semibold">Name</th>
                          <th className="text-right p-3 font-semibold">Age</th>
                          <th className="text-right p-3 font-semibold">Holiday</th>
                          <th className="text-right p-3 font-semibold">Sick days</th>
                          <th className="text-right p-3 font-semibold">Occasions</th>
                          <th className="text-right p-3 font-semibold">Unpaid</th>
                          <th className="text-right p-3 font-semibold">Needs review</th>
                          <th className="text-right p-3 font-semibold">
                            {report?.sickness_frame?.label || 'Sick to date'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.map((r) => (
                          <tr key={r.user_id}
                              className={`border-t border-border ${r.needs_attention ? 'bg-amber-500/5' : ''}`}>
                            <td className="p-3">{r.needs_attention ? '⚠️' : ''}</td>
                            <td className={`p-3 ${r.missing_payroll_id ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {r.payroll_id || '— none —'}
                            </td>
                            <td className="p-3">
                              <div className="font-medium">{r.name}</div>
                              {r.left_in_period && (
                                <div className="text-xs text-blue-600">Left {ukDate(r.end_date)} — settle holiday</div>
                              )}
                              {r.missing_payroll_id && (
                                <div className="text-xs text-red-600">No payroll reference</div>
                              )}
                            </td>
                            {/*
                              ⚠️ The age, and WHEN it changed — never a wage band.
                              UK minimum wage steps at 18 and 21, so a birthday
                              inside a pay period really can change what somebody
                              must be paid; naming the band would be us encoding
                              employment law that changes every April and differs
                              by country. We state the fact, payroll knows the law.
                            */}
                            <td className="p-3 text-right">
                              {r.age ?? '—'}
                              {r.birthday_in_period && (
                                <div className="text-xs font-medium text-purple-600 whitespace-nowrap">
                                  turns {r.turns_age} on {ukDate(r.birthday_in_period)}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {num(r.holiday_days)}
                              {r.holiday_hours ? <div className="text-xs text-muted-foreground">{num(r.holiday_hours)} hrs</div> : null}
                            </td>
                            <td className="p-3 text-right">
                              {num(r.sick_days)}
                              {r.sick_hours ? <div className="text-xs text-muted-foreground">{num(r.sick_hours)} hrs</div> : null}
                            </td>
                            <td className="p-3 text-right">{r.sick_occasions || '—'}</td>
                            <td className="p-3 text-right">
                              {r.unpaid_days ? (
                                <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-600 text-xs font-semibold">
                                  {num(r.unpaid_days)}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="p-3 text-right">{num(r.review_days)}</td>
                            <td className="p-3 text-right">{r.sick_to_date_days || '—'}</td>
                          </tr>
                        ))}
                        {summary.length === 0 && (
                          <tr>
                            <td colSpan={10} className="p-10 text-center text-muted-foreground">
                              No absence in this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm min-w-[900px]">
                      <thead>
                        <tr className="bg-secondary/30 text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="text-left p-3 font-semibold">Ref</th>
                          <th className="text-left p-3 font-semibold">Name</th>
                          <th className="text-left p-3 font-semibold">Type</th>
                          <th className="text-left p-3 font-semibold">From</th>
                          <th className="text-left p-3 font-semibold">To</th>
                          <th className="text-right p-3 font-semibold">Days</th>
                          <th className="text-right p-3 font-semibold">Hours</th>
                          <th className="text-left p-3 font-semibold">Pay treatment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(report?.detail || []).map((r, i) => (
                          <tr key={`${r.user_id}-${r.from}-${i}`} className="border-t border-border">
                            <td className="p-3 text-muted-foreground">{r.payroll_id || '— none —'}</td>
                            <td className="p-3 font-medium">{r.name}</td>
                            <td className="p-3">
                              {r.leave_type_display}
                              {/*
                                ⚠️ The part-day label is what stops the days
                                column reading as wrong. A morning off is NOT
                                half a day when the sessions are unequal — on a
                                09:00–13:00 + 14:00–17:00 pattern it is 4 hours
                                of a 7-hour day, so 0.57 days. Correct, and
                                baffling without this line.
                              */}
                              {r.part_day && (
                                <div className="text-xs font-medium text-amber-600">
                                  {r.part_day.replace(/^\(|\)$/g, '')}
                                </div>
                              )}
                              {r.clipped && (
                                <div className="text-xs text-muted-foreground">
                                  part of a longer absence
                                </div>
                              )}
                            </td>
                            <td className="p-3">{ukDate(r.from)}</td>
                            <td className="p-3">{ukDate(r.to)}</td>
                            <td className="p-3 text-right">{num(r.days)}</td>
                            <td className="p-3 text-right">{num(r.hours)}</td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${treatmentClass(r.pay_treatment)}`}>
                                {treatmentLabel(r.pay_treatment)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {(report?.detail || []).length === 0 && (
                          <tr>
                            <td colSpan={8} className="p-10 text-center text-muted-foreground">
                              No absence in this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Said out loud rather than left to be discovered. */}
                <div className="p-4 bg-secondary/20 border-t border-border text-xs text-muted-foreground flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-none" />
                  <span>{(report?.caveats || []).join(' ')} Reasons are never included.</span>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default PayrollPage;
